import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { HbrLineChart, HbrSpinner } from '@harbor/elements-react-standalone';
import { TileDefinition } from '../../../types/dashboard';
import type { TileSettings } from '../../../types/tileBuilderTypes';
import { buildHarborItemTooltip } from '../../../support/harborTooltip';
import { applyCommonHarborRuntimeInModifyConfig } from '../../../support/harborChartRuntimeUtils';
import {
  getContextualChartMargins,
  measureChartTextWidth,
} from './chartLayoutUtils';
import { makeSmartValueFormatter } from './tileUtils';

interface LineChartTileProps {
  data: any;
  tileDef: TileDefinition;
  layoutHeight: number;
  layoutWidth: number;
  layoutVersion?: number;
  setError?: (error: string | null) => void;
  initialized?: boolean;
  isPreview?: boolean;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

const LABEL_FONT = '14px Inter, Arial, sans-serif';
const DEFAULT_MARGIN = { top: 50, right: 60, bottom: 50, left: 50 };

function clampLineMargins(margin: any, width: number, height: number) {
  const MIN_PLOT_W = 20;
  const MIN_PLOT_H = 20;

  const m = { ...margin };

  m.top = Math.max(0, Number(m.top ?? 0));
  m.right = Math.max(0, Number(m.right ?? 0));
  m.bottom = Math.max(0, Number(m.bottom ?? 0));
  m.left = Math.max(0, Number(m.left ?? 0));

  const maxRight = Math.max(0, width - m.left - MIN_PLOT_W);
  m.right = Math.min(m.right, maxRight);

  const maxBottom = Math.max(0, height - m.top - MIN_PLOT_H);
  m.bottom = Math.min(m.bottom, maxBottom);

  const maxLeft = Math.max(0, width - m.right - MIN_PLOT_W);
  m.left = Math.min(m.left, maxLeft);

  const maxTop = Math.max(0, height - m.bottom - MIN_PLOT_H);
  m.top = Math.min(m.top, maxTop);

  return m;
}

function buildConfig({
                       lineSeries,
                       tileDef,
                       width,
                       height,
                       itemTooltip,
                       configVersion,
                       valueFormatter,
                       displayContext,
                     }: {
  lineSeries: any[];
  tileDef: TileDefinition;
  width: number;
  height: number;
  itemTooltip?: any;
  configVersion: number;
  valueFormatter?: (v: number) => string;
  displayContext: 'preview' | 'dashboard' | 'leader';
}) {
  const s = (tileDef.settings || {}) as TileSettings & any;

  const showLegend = (s.legendEnabled ?? s.show_legend) === true;

  const layoutStrategy: 'auto' | 'manual' =
    (s as any)?.layoutStrategy === 'manual' ? 'manual' : 'auto';

  const margin: any =
    layoutStrategy === 'manual'
      ? getContextualChartMargins(s, displayContext, showLegend, DEFAULT_MARGIN)
      : {
        top: s.marginTop ?? DEFAULT_MARGIN.top,
        bottom: s.marginBottom ?? DEFAULT_MARGIN.bottom,
        right: s.marginRight ?? DEFAULT_MARGIN.right,
        left: s.marginLeft ?? DEFAULT_MARGIN.left,
      };

  const axisBottom: any = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: s.axisBottomRotation ?? 0,
    legend: s.axisBottomLegend ?? null,
    legendOffset: s.axisBottomLegendOffset ?? 0,
    truncateTickAt: (s as any).truncateTickAt ?? 0,
    ...(s.axisBottom || {}),
  };

  const axisLeft: any = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: s.axisLeftRotation ?? 0,
    legend: s.axisLeftLegend ?? null,
    legendOffset: s.axisLeftLegendOffset ?? 0,
    ...(s.axisLeft || {}),
    ...(valueFormatter ? { format: valueFormatter } : {}),
  };

  const legends: any[] = showLegend
    ? [
      {
        anchor: s.legendAnchor ?? 'bottom',
        direction: s.legendDirection ?? 'row',
        translateX: s.legendTranslateX ?? 0,
        translateY: s.legendTranslateY ?? 0,
        itemWidth: s.legendItemWidth ?? 80,
        itemHeight: s.legendItemHeight ?? 20,
        symbolSize: s.legendSymbolSize ?? 18,
        itemsSpacing: s.legendItemsSpacing ?? 5,
      },
    ]
    : [];

  if (layoutStrategy !== 'manual') {
    if (width > 200) {
      try {
        const sampleSeries =
          Array.isArray(lineSeries) && lineSeries.length > 0 ? lineSeries[0] : null;
        const xs = Array.isArray(sampleSeries?.data)
          ? sampleSeries.data.map((pt: any) => String(pt.x ?? ''))
          : [];
        const truncateAt = axisBottom.truncateTickAt || 0;
        const labels = xs.map((t) =>
          truncateAt > 0 && t.length > truncateAt ? t.slice(0, truncateAt) + '…' : t
        );
        const longestTickWidth = labels.reduce(
          (max, text) => Math.max(max, measureChartTextWidth(text, LABEL_FONT, 8)),
          0
        );
        const rightPaddingForTicks =
          Math.ceil(longestTickWidth / 2) + (axisBottom.tickPadding ?? 5) + 8;
        margin.right = Math.max(margin.right, rightPaddingForTicks);
      } catch {
        // ignore
      }
    }

    if (showLegend && legends[0]?.anchor === 'bottom') {
      const legendFootprint = Math.ceil(
        (legends[0].itemHeight ?? 20) + (legends[0].translateY ?? 0) + 8
      );
      margin.bottom = Math.max(margin.bottom, legendFootprint);
    }
    if (showLegend && legends[0]?.anchor === 'right') {
      const legendFootprintRight = Math.ceil(
        (legends[0].itemWidth ?? 80) + (legends[0].translateX ?? 0) + 12
      );
      margin.right = Math.max(margin.right, legendFootprintRight);
    }

    const maxTotalVerticalMargin = Math.floor(height * 0.25);
    const maxSingleVerticalMargin = Math.floor(height * 0.18);
    margin.top = Math.min(margin.top, maxSingleVerticalMargin);
    margin.bottom = Math.min(margin.bottom, maxSingleVerticalMargin);
    if (margin.top + margin.bottom > maxTotalVerticalMargin) {
      const excess = margin.top + margin.bottom - maxTotalVerticalMargin;
      const reduceBottom = Math.min(excess, Math.max(0, margin.bottom - 10));
      margin.bottom -= reduceBottom;
      const remainingExcess = excess - reduceBottom;
      if (remainingExcess > 0) margin.top = Math.max(10, margin.top - remainingExcess);
    }
  }

  const safeMargin = clampLineMargins(margin, width, height);

  const xScale: any = {
    type: s.xScaleType || 'point',
    ...(s.xScale || {}),
    __v: configVersion,
  };

  const yScale: any = {
    type: s.yScaleType || 'linear',
    min: s.yScaleMin ?? 'auto',
    max: s.yScaleMax ?? 'auto',
    stacked: s.yScaleStacked ?? false,
    reverse: s.yScaleReverse ?? false,
    ...(s.yScale || {}),
  };

  return {
    data: lineSeries,
    xScale,
    yScale,

    height: Math.max(1, height),
    width: Math.max(1, width),

    margin: safeMargin,
    axisBottom,
    axisLeft,

    enableArea: s.enableArea ?? false,
    areaOpacity: s.areaOpacity ?? 0.35,
    areaBaselineValue: s.areaBaselineValue ?? 0,
    enableSlices: s.enableSlices ?? 'x',
    curve: s.lineCurve ?? 'linear',
    useMesh: s.useMesh ?? true,

    isInteractive: s.isInteractive ?? true,
    isFocusable: s.isFocusable ?? true,

    enablePointLabel: s.enablePointLabel ?? false,
    pointLabelYOffset: s.pointLabelYOffset ?? 0,
    pointSize: s.pointSize ?? 6,
    pointBorderWidth: s.pointBorderWidth ?? 1,
    pointBorderColor: s.pointBorderColor ?? '#fff',
    pointColor: s.pointColor ?? undefined,

    animate: s.animate ?? true,

    legends,
    itemTooltip,
  };
}

const LineChartTile: React.FC<LineChartTileProps> = ({
                                                       tileDef,
                                                       data,
                                                       layoutHeight,
                                                       layoutWidth,
                                                       layoutVersion,
                                                       setError,
                                                       initialized = false,
                                                       isPreview = false,
                                                       displayContext = 'dashboard',
                                                     }) => {
  const s = useMemo(() => (tileDef?.settings ?? {}) as TileSettings & any, [tileDef?.settings]);
  const useCategorical = (s as any).useCategorical !== false;

  const popoverOptions = useMemo(
    () => ({
      hoist: (s as any)?.tooltipHoist !== false,
      placement: (s as any)?.tooltipPlacement ?? 'top',
      distance: typeof (s as any)?.tooltipDistance === 'number' ? (s as any).tooltipDistance : 8,
      skidding: typeof (s as any)?.tooltipSkidding === 'number' ? (s as any).tooltipSkidding : 0,
    }),
    [s]
  );

  const valueFormatter = useMemo(
    () =>
      makeSmartValueFormatter({
        valueFormat: (s as any).valueFormat,
        currency: (s as any).currency,
        numberNotation: (s as any).numberNotation,
        compactThreshold: (s as any).compactThreshold,
        compactDecimals: (s as any).compactDecimals,
        prefixCurrencyOnCompact: (s as any).prefixCurrencyOnCompact,
      }),
    [s]
  );

  const lineSeries = useMemo(() => {
    if (data) {
      if (!data?.[0]?.['id']) {
        return [
          {
            id: tileDef.name || 'Series',
            data: Array.isArray(data) ? data.map((pt: any) => ({ x: pt.x, y: pt.y })) : [],
          },
        ];
      }
      return (Array.isArray(data) ? data : []).map((series: any) => ({
        ...series,
        data: Array.isArray(series.data)
          ? series.data.map((pt: any) => ({ x: pt.x, y: pt.y }))
          : [],
      }));
    }
    return [{ id: tileDef.name || 'Series', data: [] }];
  }, [tileDef.name, data]);

  const hasData = useMemo(() => {
    return (
      Array.isArray(lineSeries) &&
      lineSeries.some((s: any) => Array.isArray(s.data) && s.data.length > 0)
    );
  }, [lineSeries]);

  const sizerRef = useRef<HTMLDivElement>(null);
  const [innerW, setInnerW] = useState(0);
  const [innerH, setInnerH] = useState(0);

  useEffect(() => {
    if (layoutWidth > 0) setInnerW(layoutWidth);
    if (layoutHeight > 0) setInnerH(layoutHeight);
  }, [layoutWidth, layoutHeight]);

  useEffect(() => {
    if (!sizerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        const h = Math.floor(entry.contentRect.height);
        if (w > 0) setInnerW((prev) => (prev !== w ? w : prev));
        if (h > 0) setInnerH((prev) => (prev !== h ? h : prev));
      }
    });
    ro.observe(sizerRef.current);
    return () => ro.disconnect();
  }, []);

  const itemTooltip = useMemo(() => {
    return buildHarborItemTooltip(
      s,
      {
        getSeries: (e: any) =>
          e?.serieId ?? e?.id ?? e?.key ?? e?.data?.serieId ?? e?.data?.id ?? e?.data?.key ?? '',
        getLabel: (e: any) =>
          e?.data?.xFormatted ??
          e?.data?.x ??
          e?.point?.data?.xFormatted ??
          e?.point?.data?.x ??
          '',
        getValue: (e: any) => {
          const raw =
            e?.data?.y ??
            e?.point?.data?.y ??
            e?.data?.yFormatted ??
            e?.point?.data?.yFormatted ??
            '';
          const n = Number(raw);
          return Number.isFinite(n) ? valueFormatter(n) : raw;
        },
      },
      { dotMode: 'before' }
    );
  }, [s, valueFormatter]);

  const [configVersion, setConfigVersion] = useState(0);
  const lastLayoutVersionRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isPreview) return;
    if (innerW <= 10 || innerH <= 10) return;

    const lv = layoutVersion ?? 0;
    if (lastLayoutVersionRef.current === lv) return;

    lastLayoutVersionRef.current = lv;
    setConfigVersion((v) => v + 1);
  }, [isPreview, innerW, innerH, layoutVersion]);

  const config = useMemo(() => {
    const width = innerW > 0 ? innerW : Math.max(1, layoutWidth);
    const height = innerH > 0 ? innerH : Math.max(1, layoutHeight);

    return buildConfig({
      lineSeries,
      tileDef,
      width,
      height,
      itemTooltip,
      configVersion,
      valueFormatter,
      displayContext,
    });
  }, [
    lineSeries,
    tileDef,
    innerW,
    innerH,
    layoutWidth,
    layoutHeight,
    itemTooltip,
    configVersion,
    valueFormatter,
    displayContext,
  ]);

  const modifyHbrConfig = useCallback(
    (finalCfg: any, _createElement: any, util: any) => {
      const merged = applyCommonHarborRuntimeInModifyConfig(
        finalCfg,
        {
          grid: {
            enableGridX: !!(s as any).enableGridX,
            enableGridY: (s as any).enableGridY !== false,
            gridXValues: (s as any).gridXValues,
            gridYValues: (s as any).gridYValues,
          },
          axes: {
            xAxisLabel: (s as any).xAxisLabel,
            yAxisLabel: (s as any).yAxisLabel,
            axisBottomLegend: (s as any).axisBottomLegend,
            axisLeftLegend: (s as any).axisLeftLegend,
            axisBottomTickValues: (s as any).axisBottomTickValues,
            axisLeftTickValues: (s as any).axisLeftTickValues,
          },
        },
        util,
        { kind: 'line', defaultColorBy: 'id' }
      );

      merged.axisLeft = { ...(merged.axisLeft || {}), format: valueFormatter };

      merged.popoverOptions = {
        ...(merged.popoverOptions || {}),
        hoist: (s as any)?.tooltipHoist !== false,
        placement: (s as any)?.tooltipPlacement ?? 'top',
        distance: typeof (s as any)?.tooltipDistance === 'number' ? (s as any).tooltipDistance : 8,
        skidding: typeof (s as any)?.tooltipSkidding === 'number' ? (s as any).tooltipSkidding : 0,
      };
      merged.tooltipZIndex = merged.tooltipZIndex ?? 9999;

      return merged;
    },
    [s, valueFormatter]
  );

  useEffect(() => {
    if (!setError) return;
    if (!initialized) return;
    setError(null);
  }, [setError, initialized, hasData]);

  if (!initialized) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HbrSpinner>Loading ...</HbrSpinner>
      </div>
    );
  }

  return (
    <div
      ref={sizerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 100,
        minWidth: 0,
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      <HbrLineChart
        key={`line-${displayContext}-${layoutVersion ?? 0}-${configVersion}-${Math.max(
          innerW,
          layoutWidth
        )}-${Math.max(innerH, layoutHeight)}-${hasData ? 'data' : 'empty'}`}
        categorical={useCategorical}
        config={config}
        modifyHbrConfig={modifyHbrConfig}
        popoverOptions={popoverOptions as any}
        style={{ width: '100%', height: '100%', minHeight: '100px' }}
      />
    </div>
  );
};

export default LineChartTile;